import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrentUser } from './useCurrentUser'
import AuthLoading from './AuthLoading'
import AccessRestrictedPage from '../pages/AccessRestrictedPage'
import EmptyState from '../components/ui/EmptyState'
import Button from '../components/ui/Button'

/**
 * HR erişim kapısı (099/research.md D7): `RequireAuth`'un hemen ardından, normal `<Routes>` ağacı
 * kurulmadan ÖNCE. `/me` henüz yüklenmemişse nötr yükleme (flash önleme — `RequireAuth`'taki
 * `AuthLoading` deseniyle tutarlı); yüklendiyse ve `accessRestricted: true` ise dedike bir sayfa
 * gösterilir — menü/route/API çağrıları hiç kurulmaz. Diğer tüm durumlarda normal kabuk (children)
 * kurulur.
 *
 * <p><b>`/me` başarısız olursa (ağ/5xx, `main.tsx`'teki 3 retry tükendikten sonra) normal kabuk
 * DA KURULMAZ</b> (fail-closed — Duo code review bulgusu, MR !198): `isError` true iken `data`
 * `undefined` kalır ve `data?.accessRestricted` yanlışlıkla "kısıtlı değil" gibi yorumlanabilirdi;
 * kısıtlı bir kullanıcı geçici bir ağ sorununda kapıyı atlayıp `AppLayout`'a (ör. grant istemeyen
 * `/notifications`'a) düşebilirdi. Bunun yerine tekrar-dene aksiyonlu nötr bir hata durumu gösterilir.
 */
export default function AccessGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } = useCurrentUser()
  if (isLoading) {
    return <AuthLoading />
  }
  if (isError) {
    return (
      <div className="page">
        <EmptyState
          icon="pi pi-exclamation-triangle"
          title={t('shell.accessCheckFailed.title')}
          description={t('shell.accessCheckFailed.desc')}
          action={<Button label={t('common.errors.retry')} onClick={() => refetch()} />}
        />
      </div>
    )
  }
  if (data?.accessRestricted) {
    return <AccessRestrictedPage />
  }
  return <>{children}</>
}
